import { Box, Stack, AppBar, Toolbar, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NextLink from 'next/link';

import Avatar from '@subflow-frontend/components/Avatar';
import { useSubdomain } from '@subflow-frontend/sections/subscriber';
import { useSubscriberOrgs } from '@subflow/data-access';
import OrgPopover from '@subflow-frontend/sections/subscriber/OrgPopover';
import createAvatar from '@subflow-frontend/utils/createAvatar';

import Image from '@subflow-frontend/components/image';
import AccountPopover from './AccountPopover';
import useOffSetTop from '@subflow-frontend/hooks/useOffSetTop';
import useResponsive from '@subflow-frontend/hooks/useResponsive';
import { bgBlur } from '@subflow-frontend/utils/cssStyles';
import { HEADER } from '@subflow-frontend/config';
import { useSubscriberPaths } from '@subflow-frontend/routes/paths';

type Props = {
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  verticalLayout = false,
}: Props) {
  const { data: orgs } = useSubscriberOrgs();
  const { subdomain } = useSubdomain();
  const currentOrg = orgs?.find(org => org.subDomain === subdomain);

  const theme = useTheme();
  const isDesktop = useResponsive('up', 'lg');
  const { subscriberPaths } = useSubscriberPaths();

  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const notificationCount = 12;

  const renderContent = (
    <Stack 
      flexDirection={'row'} 
      width={'100%'}
      justifyContent={'space-between'} 
      alignItems={'center'}
      sx={{
        maxWidth: '80rem',
        margin: 'auto',
      }}
    >
      <Stack flexDirection={'row'} alignItems={'center'} sx={{ cursor: 'pointer', gap: 1 }}>
        <NextLink href={subscriberPaths.dashboard} passHref>
          {currentOrg?.logo ? (
            <Box
              component="img"
              sx={{
                width: '40px',
                height: '40px',
                objectFit: 'cover', 
                borderRadius: '50%',
                border: 'none',
              }}
              alt="avatar"
              src={currentOrg?.logo}
            />
          ) : (
            <Avatar 
              sx={{ 
                width: '40px',
                height: '40px',
                border: 'none',
              }} 
              color={
                createAvatar(currentOrg?.businessName).color
              }
              alt={currentOrg?.businessName}
            >
              {createAvatar(currentOrg?.businessName).name}
            </Avatar>
          )}
          </NextLink>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              zIndex: 100,
            }}
          >
            <Stack sx={{ maxWidth: '400px' }}>
              <OrgPopover orgs={orgs} />
            </Stack>
          </Box>
        </Stack>
      
      <Stack 
        flexDirection={'row'} 
        alignItems={'center'} 
        sx={{ gap: '24px'}}
      >
        <Stack sx={{ display: 'none' }}>
          <Image src={'/assets/icons/search.svg'} alt={'logo'} />
        </Stack>
        <Stack sx={{ position: 'relative', display: 'none' }}>
          <Stack>
            <Image src={'/assets/icons/alert.svg'} alt={'logo'} />
          </Stack>
          <Stack>
            <Typography 
              noWrap 
              sx={{ 
                display: 'flex',
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '12px',
                lineHeight: '12px',
                fontWeight: '400',
                color: '#fff',
                background: '#FF2A84',
              }}
            >
              {notificationCount}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ m: 0 }}>
          <AccountPopover />
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <AppBar
      sx={{
        boxShadow: '-13px 21px 49px 0px rgba(0, 0, 0, 0.13)',
        height: HEADER.MOBILE_HEIGHT,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
          ...(isOffset && {
            height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
